// import node module libraries
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PURGE,
  REGISTER,
  PERSIST,
  type PersistedState,
} from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
// import storage from "redux-persist/lib/storage";

// import redux slices
import gridReducer from "./featureSlices/gridSlice";
import databaseReducer from "./featureSlices/databaseSlice";
import chatReducer from "./featureSlices/chatSlice";
import chartReducer from "./featureSlices/chartSlice";
import chatSettingReducer from "./featureSlices/chatSettingSlice";
import themeReducer from "./featureSlices/themeSlice";
import notesReducer from "./featureSlices/stickyNoteSlice";
import colorPaletteReducer from "./featureSlices/colorPaletteSlice";
import dashboarReducer from "./featureSlices/dashboardSlice";
import extraReducer from "./featureSlices/extraSlice";
import userReducer from "./featureSlices/userSlice";

// Create a noop storage for SSR
const createNoopStorage = () => {
  return {
    getItem(_key: any) {
      return Promise.resolve(null);
    },
    setItem(_key: any, _value: any) {
      return Promise.resolve();
    },
    removeItem(_key: any) {
      return Promise.resolve();
    },
  };
};

const storage =
  typeof window !== "undefined"
    ? createWebStorage("local")
    : createNoopStorage();

const persistConfig = {
  key: "root",
  version: 2,
  storage: storage,
  whitelist: [
    "grid",
    "databases",
    "chat",
    "chart",
    "chatSetting",
    "theme",
    "notes",
    "colorPalette",
    "dashboard",
    "extra",
    "user",
  ],
  migrate: (state: unknown): Promise<PersistedState> => {
    if (state == null || typeof state !== "object")
      return Promise.resolve(undefined);
    const { ui, ...rest } = state as Record<string, unknown>;
    return Promise.resolve(rest as PersistedState);
  },
};

const rootReducer = combineReducers({
  grid: gridReducer,
  databases: databaseReducer,
  chat: chatReducer,
  chart: chartReducer,
  chatSetting: chatSettingReducer,
  theme: themeReducer,
  notes: notesReducer,
  colorPalette: colorPaletteReducer,
  dashboard: dashboarReducer,
  extra: extraReducer,
  user: userReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export type RootState = ReturnType<typeof rootReducer>; // Correctly get the RootState type
export type AppDispatch = typeof store.dispatch;

export default store;
